import { GraphicsQuality } from "../../managers/ScreenManager";
import { TableData } from "../../types/types";
import { VariationId } from "../screen/TableScreen";


export enum AppDebugLogType {
    GENERIC,
    /**
     * For QA team
     */
    DEBUG,
    /**
     * Google Tag Manager logs
     */
    GTM,
    /**
     * Debug cleanup message
     */
    DESTROY,
}

/**
 * Debug flags to debug app
 * Source:
 * > GET params (html doc)
 * > code? 
 */
export class AppDebug
{
    public static DEBUG_LOGS: boolean = true;
    public static DEBUG_GETATTRIBS: boolean = false;
    private static urlSearchParams: URLSearchParams;


    public static log(message:string|string[], logType:AppDebugLogType = AppDebugLogType.GENERIC): void
    {
        switch(logType) {
            // the only type controlled by GETattribute flag
            case AppDebugLogType.DEBUG:
                if (this.IsDebug()) {
                    console.log(...message);
                }
                break;
            case AppDebugLogType.GTM:
                if (this.IsGTMDebug()) {
                    console.log(`%c[GTM]`, "color:magenta;font-weight:bold;", message);
                }
                break;
            case AppDebugLogType.GENERIC:
                if (AppDebug.DEBUG_LOGS) {
                    console.log(...message); // default color
                }
                break;
            case AppDebugLogType.DESTROY:
                if (AppDebug.DEBUG_LOGS) {
                    console.log("%c"+message, "color:orange");
                }
                break;
        }
    }

    /**
     * Add GET attributes after url:
     * "?testWim" (TRUE = 'not unidentified')
     * > debug variation win condition with custom Table
     * > sets @see TableBase.tableGenerationType to @see TableGenerationType.MANUAL
     * "?testLose" (TRUE = 'not unidentified')
     * > debug variation lose condition with custom Table
     */
    public static Init(): void
    {
        // get and parse GET params
        // Address of the current window
        const address: string = window.location.search;
        
        // Returns a URLSearchParams object instance
        this.urlSearchParams = new URLSearchParams(address);
    }

    /**
     * Return language index from GET parameters (if exists)
     * @returns -1 if testLang parameter does not exist as GET parameter.
     */
    public static GetLanguageIndex(): number
    {
        const targetParam: string = this.urlSearchParams.get("lang");
        return targetParam == undefined ? -1 : Number.parseInt(targetParam);
    }

    public static GetQualityIndex(): GraphicsQuality
    {
        const targetParam: string = this.urlSearchParams.get("quality");
        return targetParam == undefined ? GraphicsQuality.UNSET : Number.parseInt(targetParam);
    }
    
    public static GetScalingVariantScale(): number
    {
        const targetParam: string = this.urlSearchParams.get("sv");
        switch(targetParam) {
            case undefined: return 1;
            case "0": return 1;
            case "1": return 0.75;
            case "2": return 0.5;
        }
        return 1;
    }
    
    /**
     * nofs = disable FullScreen button.
     * @returns 
     */
    public static HasForceHideFullscreenBtn(): boolean
    {
        const targetParam: string = this.urlSearchParams.get("nofs");
        return targetParam == undefined ? false : true;
    }

    public static GetVariationId(defaultVariationId: VariationId = VariationId.BELOTE): VariationId
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("variationId");
            return targetParam == undefined ? defaultVariationId : Number.parseInt(targetParam);
        }
        return defaultVariationId;
    }

    /**
     * Example:
        ?trumpSuit=3&tableData={"state":{"taskType":4},"playersCardsData":[[{"rank":4,"suit":1},{"rank":4,"suit":3},{"rank":3,"suit":0},{"rank":6,"suit":1},{"rank":0,"suit":0},{"rank":7,"suit":2},{"rank":5,"suit":0},{"rank":7,"suit":3}],[{"rank":3,"suit":3},{"rank":1,"suit":3},{"rank":6,"suit":3},{"rank":0,"suit":2},{"rank":0,"suit":3},{"rank":6,"suit":2},{"rank":2,"suit":3},{"rank":4,"suit":0}],[{"rank":5,"suit":3},{"rank":3,"suit":1},{"rank":5,"suit":1},{"rank":1,"suit":1},{"rank":3,"suit":2},{"rank":5,"suit":2},{"rank":7,"suit":0},{"rank":2,"suit":0}],[{"rank":1,"suit":2},{"rank":0,"suit":1},{"rank":2,"suit":2},{"rank":4,"suit":2},{"rank":2,"suit":1},{"rank":6,"suit":0},{"rank":7,"suit":1},{"rank":1,"suit":0}]]}
     */
    public static GetTableData(): TableData
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: TableData = JSON.parse(this.urlSearchParams.get("tableData")) as TableData;
            return targetParam == undefined ? null : targetParam;
        }
        return null;
    }

    public static IsDebug(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("debug");
            return targetParam == undefined ? false : true;
        }
        return false;
    }

    /**
     * Using gtm debug URLParam displays GTM debu logs (if any; @see GTMTracking)
     */
    public static IsGTMDebug(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("gtm");
            return targetParam == undefined ? false : true;
        }
        return false;
    }

    public static SkipLoadingScreen(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("skipLoad");
            return targetParam == undefined ? false : true;
        }
        return false;
    }

    public static IsFastestSpeed(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("fastest");
            return targetParam == undefined ? false : true;
        }
        return false;
    }

    /**
     * show cards of all players
     */
    public static IsShowCards(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("showCards");
            return targetParam == undefined ? false : true;
        }
        return false;
    }

    public static CanSaveTable(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("noSave");
            return targetParam == undefined ? true : false;
        }
        return true;
    }

    public static GetScreenScale(defaultValue: GraphicsQuality): number
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: number = parseInt(this.urlSearchParams.get("scale"));
            // console.log(Number.isNaN(targetParam), targetParam);
            return Number.isNaN(targetParam) ? defaultValue : targetParam;
        }
        return defaultValue;
    }

    public static GetDebugMajorDeclarations(): boolean
    {
        if (this.DEBUG_GETATTRIBS) {
            const targetParam: string = this.urlSearchParams.get("debugDecl");
            return targetParam == undefined ? false : true;
        }
        return false;
    }
}